import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';

interface Legislation {
    id: string;
    name: string;
    type: string;
    status: 'ACTIVE' | 'RETIRED';
    dateOfEffect: string;
    actions: string;
    notes: string;
}

export default function RegulationList() {
    const [legislations, setLegislations] = useState<Legislation[]>([]);
    const [loading, setLoading] = useState(true);
    const [showUpload, setShowUpload] = useState(false);
    const [uploadMode, setUploadMode] = useState<'manual' | 'bulk'>('manual');
    const [bulkData, setBulkData] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        type: '',
        status: 'ACTIVE',
        dateOfEffect: new Date().toISOString().split('T')[0],
        actions: '',
        notes: ''
    });

    useEffect(() => {
        fetchLegislations();
    }, []);

    const fetchLegislations = async () => {
        try {
            setLoading(true);
            const res = await adminApi.get('api/super-admin/legislations');
            setLegislations(res.data.legislations || []);
        } catch (error) {
            console.error('Failed to fetch legislations', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            await adminApi.post('api/super-admin/regulations', {
                ...formData,
                legislationType: true
            });
            setShowUpload(false);
            fetchLegislations();
            setFormData({
                name: '',
                type: '',
                status: 'ACTIVE',
                dateOfEffect: new Date().toISOString().split('T')[0],
                actions: '',
                notes: ''
            });
            alert('Legislation uploaded successfully');
        } catch (error) {
            console.error('Create failed', error);
            alert('Failed to upload legislation');
        }
    };

    const handleBulkUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const legislations = JSON.parse(bulkData);
            const res = await adminApi.post('api/super-admin/legislations/bulk', { legislations });
            if (res.data.success || res.data.summary.successful > 0) {
                setShowUpload(false);
                fetchLegislations();
                setBulkData('');
                alert(`${res.data.summary.successful} legislations uploaded${res.data.summary.failed > 0 ? `, ${res.data.summary.failed} failed` : ''}`);
            } else {
                alert(res.data.message);
            }
        } catch (error) {
            console.error('Bulk upload failed', error);
            alert('Failed to parse or upload legislations. Ensure JSON format is correct.');
        }
    };

    const toggleStatus = async (id: string, currentStatus: string) => {
        const newStatus = currentStatus === 'ACTIVE' ? 'RETIRED' : 'ACTIVE';
        try {
            await adminApi.patch(`api/super-admin/regulations/${id}/status`, { status: newStatus });
            fetchLegislations();
        } catch (error) {
            console.error('Status update failed', error);
            alert('Failed to update status');
        }
    };

    if (loading) return <div className="p-6 text-center">Loading Legislations...</div>;

    return (
        <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Legislations Registry</h2>
                    <p className="text-sm text-gray-600 mt-1">AI-consumable legislation database for future compliance automation</p>
                </div>
                <button
                    onClick={() => setShowUpload(!showUpload)}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 font-medium"
                >
                    {showUpload ? 'Cancel' : '+ Upload Legislation'}
                </button>
            </div>

            {showUpload && (
                <div className="mb-8 bg-gray-50 p-6 rounded border-2 border-blue-200">
                    <div className="mb-4 flex gap-4">
                        <label className="flex items-center">
                            <input
                                type="radio"
                                value="manual"
                                checked={uploadMode === 'manual'}
                                onChange={(e) => setUploadMode(e.target.value as 'manual' | 'bulk')}
                                className="mr-2"
                            />
                            <span className="text-sm font-medium">Manual Entry</span>
                        </label>
                        <label className="flex items-center">
                            <input
                                type="radio"
                                value="bulk"
                                checked={uploadMode === 'bulk'}
                                onChange={(e) => setUploadMode(e.target.value as 'manual' | 'bulk')}
                                className="mr-2"
                            />
                            <span className="text-sm font-medium">Bulk JSON Upload</span>
                        </label>
                    </div>

                    {uploadMode === 'manual' ? (
                        <form onSubmit={handleCreate}>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Name *</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border rounded"
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g., Renters' Rights Act 2025"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Type *</label>
                                    <input
                                        type="text"
                                        className="w-full p-2 border rounded"
                                        value={formData.type}
                                        onChange={e => setFormData({ ...formData, type: e.target.value })}
                                        placeholder="e.g., Legislation, Regulation, Standard"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Date of Effect</label>
                                    <input
                                        type="date"
                                        className="w-full p-2 border rounded"
                                        value={formData.dateOfEffect}
                                        onChange={e => setFormData({ ...formData, dateOfEffect: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Status</label>
                                    <select
                                        className="w-full p-2 border rounded"
                                        value={formData.status}
                                        onChange={e => setFormData({ ...formData, status: e.target.value as 'ACTIVE' | 'RETIRED' })}
                                    >
                                        <option value="ACTIVE">Active</option>
                                        <option value="RETIRED">Retired</option>
                                    </select>
                                </div>
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Actions/Description</label>
                                <textarea
                                    className="w-full p-2 border rounded"
                                    rows={3}
                                    value={formData.actions}
                                    onChange={e => setFormData({ ...formData, actions: e.target.value })}
                                    placeholder="AI Scan: ..., Audit: ..., etc."
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700">Notes</label>
                                <textarea
                                    className="w-full p-2 border rounded"
                                    rows={2}
                                    value={formData.notes}
                                    onChange={e => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder="Optional implementation notes..."
                                />
                            </div>
                            <button type="submit" className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 font-medium">
                                Save Legislation
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleBulkUpload}>
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">Paste JSON Array</label>
                                <textarea
                                    className="w-full p-3 border rounded font-mono text-sm"
                                    rows={10}
                                    value={bulkData}
                                    onChange={e => setBulkData(e.target.value)}
                                    placeholder='[{"name":"...", "type":"...", "status":"ACTIVE", "dateOfEffect":"2025-12-27", "actions":"...", "notes":"..."}]'
                                />
                            </div>
                            <button type="submit" className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 font-medium">
                                Upload Legislations
                            </button>
                        </form>
                    )}
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Name</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Date of Effect</th>
                            <th className="px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {legislations.map(leg => (
                            <tr key={leg.id} className={leg.status === 'RETIRED' ? 'bg-gray-50' : ''}>
                                <td className="px-4 py-3">
                                    <div className="font-medium text-gray-900">{leg.name}</div>
                                    {leg.actions && <div className="text-xs text-gray-600 mt-1 line-clamp-2">{leg.actions}</div>}
                                </td>
                                <td className="px-4 py-3">
                                    <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                        {leg.type}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-gray-600">
                                    {leg.dateOfEffect ? new Date(leg.dateOfEffect).toLocaleDateString() : 'N/A'}
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                                        leg.status === 'ACTIVE'
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-red-100 text-red-800'
                                    }`}>
                                        {leg.status}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm font-medium space-x-2">
                                    <button
                                        onClick={() => toggleStatus(leg.id, leg.status)}
                                        className={`px-3 py-1 rounded ${
                                            leg.status === 'ACTIVE'
                                                ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                                        }`}
                                    >
                                        {leg.status === 'ACTIVE' ? 'Retire' : 'Activate'}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {legislations.length === 0 && !loading && (
                <div className="text-center py-8 text-gray-500">
                    <p>No legislations uploaded yet. Click "Upload Legislation" to get started.</p>
                </div>
            )}
        </div>
    );
}
